import React, { memo, type PropsWithChildren } from 'react';
import { styled } from '@compiled/react';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { useIntl } from '@atlassian/jira-intl';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import messages from './messages.tsx';
import type { IssuesCountProps } from './types.tsx';

const IssuesCount = ({ totalIssues, done }: IssuesCountProps) => {
	const { formatMessage } = useIntl();
	return (
		<>
			<Flex as="dl" justifyContent="space-between" xcss={sectionWrapperStyles}>
				<IssueCountDT>
					{formatMessage(
						expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
							? messages.issuesIssueTermRefresh
							: messages.issues,
					)}
				</IssueCountDT>
				<IssueCountDD>{totalIssues}</IssueCountDD>
			</Flex>
			<Flex as="dl" justifyContent="space-between" xcss={sectionWrapperStyles}>
				<IssueCountDT>{formatMessage(messages.completed)}</IssueCountDT>
				<IssueCountDD>{done}</IssueCountDD>
			</Flex>
		</>
	);
};

export default memo<IssuesCountProps>(IssuesCount);

const sectionWrapperStyles = xcss({
	margin: '0',
	padding: '0',
	marginTop: 'space.050',
});

const issueCountLabelStyles = xcss({
	font: 'font.body',
	color: 'color.text.subtle',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueCountDTOld = styled.dt({
	fontWeight: token('font.weight.regular'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueCountDD = styled.dd({
	margin: 0,
});

export const IssueCountDT = componentWithCondition(
	isVisualRefreshEnabled,
	({ children }: PropsWithChildren<{}>) => (
		<Box as="dt" xcss={issueCountLabelStyles}>
			{children}
		</Box>
	),
	({ children }: PropsWithChildren<{}>) => <IssueCountDTOld>{children}</IssueCountDTOld>,
);
